import React, { useState } from 'react';
import { collection, getDocs, limit, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { formatMatchLocal, formatMatchTime, isMatchLocked, timeUntilLock } from '../utils/formatMatchDate';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@600;700&family=Inter:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; color: #F5F5F0; font-family: 'Inter', sans-serif; }
`;

const TIMEZONES = [
  { pais: '🇨🇴 Colombia',    tz: 'America/Bogota' },
  { pais: '🇦🇷 Argentina',   tz: 'America/Argentina/Buenos_Aires' },
  { pais: '🇲🇽 México',      tz: 'America/Mexico_City' },
  { pais: '🇪🇸 España',      tz: 'Europe/Madrid' },
  { pais: '🇻🇪 Venezuela',   tz: 'America/Caracas' },
  { pais: '🇨🇱 Chile',       tz: 'America/Santiago' },
  { pais: '🇵🇪 Perú',        tz: 'America/Lima' },
  { pais: '🇪🇨 Ecuador',     tz: 'America/Guayaquil' },
  { pais: '🇧🇷 Brasil',      tz: 'America/Sao_Paulo' },
  { pais: '🇺🇾 Uruguay',     tz: 'America/Montevideo' },
  { pais: '🇵🇦 Panamá',      tz: 'America/Panama' },
];

function formatForTZ(timestamp, tz) {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('es', {
    timeZone: tz,
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(date);
}

export default function TimezonePage() {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [loaded, setLoaded] = useState(false);

  async function loadFixtures() {
    setLoading(true);
    try {
      const q = query(collection(db, 'fixtures'), orderBy('matchNumber'), limit(10));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFixtures(list);
      setSelectedFixture(list[0] || null);
      setLoaded(true);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // Timezone actual del dispositivo
  const myTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const myLocale = Intl.DateTimeFormat().resolvedOptions().locale;

  return (
    <div style={{ minHeight: '100vh', background: '#080810', padding: '24px 20px 60px', maxWidth: 480, margin: '0 auto' }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>
          🌎 PRUEBA DE <span style={{ color: '#F0B429' }}>TIMEZONES</span>
        </div>
        <div style={{ fontSize: 12, color: '#6B6B7B' }}>
          Verifica que las horas se muestren correctas en cada país
        </div>
      </div>

      {/* Tu timezone actual */}
      <div style={{ background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.2)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 1, marginBottom: 4 }}>TU DISPOSITIVO AHORA</div>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: '#F0B429' }}>📍 {myTZ}</div>
        <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Locale: {myLocale}</div>
        <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
          Hora actual: {new Intl.DateTimeFormat('es', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }).format(new Date())}
        </div>
      </div>

      {/* Cargar partidos */}
      {!loaded ? (
        <button onClick={loadFixtures} disabled={loading}
          style={{ width: '100%', padding: '14px', background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 12, fontFamily: "'Oswald',sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: 2, color: '#080810', cursor: 'pointer', marginBottom: 20 }}>
          {loading ? 'CARGANDO...' : '⚽ CARGAR PARTIDOS DE FIRESTORE'}
        </button>
      ) : (
        <>
          {/* Selector de partido */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 1, marginBottom: 8 }}>SELECCIONA UN PARTIDO</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {fixtures.map(f => (
                <button key={f.id} onClick={() => setSelectedFixture(f)}
                  style={{ padding: '10px 14px', background: selectedFixture?.id === f.id ? 'rgba(240,180,41,0.15)' : '#14141f', border: `1px solid ${selectedFixture?.id === f.id ? 'rgba(240,180,41,0.4)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 10, textAlign: 'left', cursor: 'pointer', color: '#F5F5F0' }}>
                  <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>
                    #{f.matchNumber} {f.homeTeamName} vs {f.awayTeamName}
                  </span>
                  <span style={{ fontSize: 11, color: '#6B6B7B', marginLeft: 8 }}>{f.city}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Resultado del partido seleccionado */}
          {selectedFixture && (
            <div>
              {/* Info del partido */}
              <div style={{ background: '#14141f', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, letterSpacing: 1, marginBottom: 6 }}>
                  {selectedFixture.homeTeamName} vs {selectedFixture.awayTeamName}
                </div>
                <div style={{ fontSize: 12, color: '#6B6B7B', marginBottom: 4 }}>
                  🏟 {selectedFixture.stadium} · {selectedFixture.city}
                </div>
                <div style={{ fontSize: 11, color: '#555', fontFamily: "'Inter',sans-serif" }}>
                  UTC guardado: {selectedFixture.kickoffTimestamp?.toDate?.()?.toISOString() || '—'}
                </div>
                <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(240,180,41,0.06)', borderRadius: 8 }}>
                  <div style={{ fontSize: 10, color: '#6B6B7B', letterSpacing: 1, marginBottom: 3 }}>EN TU DISPOSITIVO</div>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, color: '#F0B429' }}>
                    {formatMatchLocal(selectedFixture.kickoffTimestamp, { showTimezone: true })}
                  </div>
                  <div style={{ fontSize: 11, color: isMatchLocked(selectedFixture.lockTimestamp) ? '#E63946' : '#06B86A', marginTop: 4 }}>
                    {isMatchLocked(selectedFixture.lockTimestamp)
                      ? '🔒 Predicciones cerradas'
                      : `⏱ Cierra en: ${timeUntilLock(selectedFixture.lockTimestamp)}`}
                  </div>
                </div>
              </div>

              {/* Tabla por país */}
              <div style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 1, marginBottom: 10 }}>HORA EN CADA PAÍS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TIMEZONES.map(({ pais, tz }) => {
                  const hora = formatForTZ(selectedFixture.kickoffTimestamp, tz);
                  const isMyTZ = myTZ.includes(tz.split('/')[1]) || tz === myTZ;
                  return (
                    <div key={tz} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: isMyTZ ? 'rgba(240,180,41,0.08)' : '#14141f', border: `1px solid ${isMyTZ ? 'rgba(240,180,41,0.2)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 10 }}>
                      <span style={{ fontSize: 13, color: isMyTZ ? '#F0B429' : '#aaa' }}>{pais}</span>
                      <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, color: isMyTZ ? '#F0B429' : '#F5F5F0', letterSpacing: 0.5 }}>{hora}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}